import React from "react";
import { Page } from "../components/App";
import {
  Button,
  Card,
  CardFooter,
  CardHeader,
  Caption1,
  Text,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import { DocumentPdf24Regular, NoteAdd24Regular } from "@fluentui/react-icons";
import { taskPaneBackground } from "../theme";

const useStyles = makeStyles({
  root: {
    height: "88vh",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    overflowY: "auto",
    ...taskPaneBackground,
    ...shorthands.padding(tokens.spacingVerticalXXL, tokens.spacingHorizontalXL),
  },
  content: {
    width: "100%",
    maxWidth: "360px",
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap(tokens.spacingVerticalXL),
    flexShrink: 0,
    marginTop: "auto",
    marginBottom: "auto",
  },
  header: {
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    ...shorthands.gap(tokens.spacingVerticalS),
  },
  trustCaption: {
    color: tokens.colorNeutralForeground4,
  },
  optionsStack: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap(tokens.spacingVerticalM),
  },
  optionCard: {
    ...shorthands.border("1px", "solid", "#dbe4ef"),
    boxShadow: tokens.shadow4,
    ...shorthands.borderRadius("14px"),
    overflow: "hidden",
    transitionProperty: "transform, box-shadow",
    transitionDuration: "150ms",
    ":hover": {
      boxShadow: tokens.shadow8,
      transform: "translateY(-2px)",
    },
  },
  iconWrapper: {
    width: "40px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  footerActions: {
    display: "flex",
    justifyContent: "space-between",
    ...shorthands.gap(tokens.spacingHorizontalM),
  },
  navButton: {
    flex: 1,
  },
});

export const MemberDataReportOptionsPage: React.FC<{ onNavigate: (page: Page) => void }> = ({
  onNavigate,
}) => {
  const styles = useStyles();

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        <div className={styles.header}>
          <Text as="h1" weight="semibold" size={700}>
            Report Options
          </Text>
          <Caption1 className={styles.trustCaption}>
            Choose how you would like to present the extracted data.
          </Caption1>
        </div>

        <div className={styles.optionsStack}>
          <Card appearance="filled" className={styles.optionCard}>
            <CardHeader
              image={
                <div className={styles.iconWrapper} aria-hidden>
                  <NoteAdd24Regular />
                </div>
              }
              header={<Text weight="semibold">Create summary letter</Text>}
              description="Draft a letter outlining member highlights and pending items."
            />
            <CardFooter>
              <Button appearance="primary" style={{ width: "100%" }}>
                Generate letter
              </Button>
            </CardFooter>
          </Card>

          <Card appearance="filled" className={styles.optionCard}>
            <CardHeader
              image={
                <div className={styles.iconWrapper} aria-hidden>
                  <DocumentPdf24Regular />
                </div>
              }
              header={<Text weight="semibold">Create draft report</Text>}
              description="Produce a draft member data report ready for review."
            />
            <CardFooter>
              <Button appearance="primary" style={{ width: "100%" }}>
                Build draft report
              </Button>
            </CardFooter>
          </Card>
        </div>

        <div className={styles.footerActions}>
          <Button
            appearance="primary"
            className={mergeClasses(styles.navButton, "sourcestone-navigation-primary")}
            onClick={() => onNavigate("dashboard")}
          >
            Back to dashboard
          </Button>
          <Button
            appearance="primary"
            className={mergeClasses(styles.navButton, "sourcestone-navigation-primary")}
            onClick={() => onNavigate("taskSelection")}
          >
            Start new job
          </Button>
        </div>
      </div>
    </div>
  );
};
